import React, { useLayoutEffect, useRef, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaSearchPlus } from "react-icons/fa";
import ImageModal from "../imageModal/ImageModal";
import "./imageSlider.css";

const SLIDE_DURATION = 400;
const SWIPE_THRESHOLD = 100;

function getImageSide(url) {
  const match = url.match(/(?:_|\/)(front|back)(?:\.|_|\/)/i);
  if (match) return match[1].charAt(0).toUpperCase() + match[1].slice(1);

  const fallback = url.match(/(front|back)/i);
  if (fallback) {
    return fallback[1].charAt(0).toUpperCase() + fallback[1].slice(1);
  }

  return null;
}

const isInteractiveTarget = (target) => {
  return target.closest("button, a");
};

const ImageSlider = ({
  imgArray = [],
  imgIdx,
  setImgIdx,
  title,
  productPage,
}) => {
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState(null);
  const [enableTransition, setEnableTransition] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const startXRef = useRef(0);
  const pointerIdRef = useRef(null);
  const viewportRef = useRef(null);

  useLayoutEffect(() => {
    const measure = () => {
      const width = viewportRef.current?.offsetWidth || 0;
      if (width > 0) {
        setViewportWidth(width);
        setIsReady(true);
      }
    };

    measure();
    window.addEventListener("resize", measure);

    return () => {
      window.removeEventListener("resize", measure);
    };
  }, []);

  if (!imgArray.length) return null;

  const prevIdx = imgIdx === 0 ? imgArray.length - 1 : imgIdx - 1;
  const nextIdx = imgIdx === imgArray.length - 1 ? 0 : imgIdx + 1;

  const prevImg = imgArray[prevIdx];
  const currentImg = imgArray[imgIdx];
  const nextImg = imgArray[nextIdx];

  const startSlide = (direction) => {
    if (animating || !viewportWidth) return;

    setAnimating(true);
    setSlideDirection(direction);
    setDragX(0);
    setEnableTransition(true);
  };

  const handlePrev = () => startSlide("prev");
  const handleNext = () => startSlide("next");

  const handlePointerDown = (e) => {
    if (isInteractiveTarget(e.target)) return;
    if (animating || !viewportWidth) return;

    startXRef.current = e.clientX;
    pointerIdRef.current = e.pointerId;
    setIsDragging(true);

    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    if (pointerIdRef.current !== e.pointerId) return;

    const delta = e.clientX - startXRef.current;
    setDragX(delta);
  };

  const handlePointerUp = (e) => {
    if (!isDragging) return;
    if (pointerIdRef.current !== e.pointerId) return;

    if (
      e.currentTarget.releasePointerCapture &&
      e.currentTarget.hasPointerCapture?.(e.pointerId)
    ) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }

    pointerIdRef.current = null;
    setIsDragging(false);

    if (dragX <= -SWIPE_THRESHOLD) {
      startSlide("next");
      return;
    }

    if (dragX >= SWIPE_THRESHOLD) {
      startSlide("prev");
      return;
    }

    setDragX(0);
  };

  const handlePointerCancel = (e) => {
    if (
      e?.currentTarget?.releasePointerCapture &&
      e.currentTarget.hasPointerCapture?.(e.pointerId)
    ) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }

    pointerIdRef.current = null;
    setIsDragging(false);
    setDragX(0);
  };

  const handleTrackTransitionEnd = () => {
    if (!slideDirection) return;

    const direction = slideDirection;

    setEnableTransition(false);

    setImgIdx((prev) => {
      if (direction === "next") {
        return prev === imgArray.length - 1 ? 0 : prev + 1;
      }

      return prev === 0 ? imgArray.length - 1 : prev - 1;
    });

    setSlideDirection(null);
    setDragX(0);
    setAnimating(false);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setEnableTransition(true);
      });
    });
  };

  const getTrackTransform = () => {
    if (!viewportWidth) {
      return "translate3d(0px, 0, 0)";
    }

    const baseX = -viewportWidth;

    if (isDragging) {
      return `translate3d(${baseX + dragX}px, 0, 0)`;
    }

    if (slideDirection === "next") {
      return `translate3d(${-2 * viewportWidth}px, 0, 0)`;
    }

    if (slideDirection === "prev") {
      return "translate3d(0px, 0, 0)";
    }

    return `translate3d(${baseX}px, 0, 0)`;
  };

  const renderSlide = (imgUrl) => {
    const sideLabel = getImageSide(imgUrl);

    return (
      <div className="is-slide">
        <img
          className={!productPage ? "is-slide-radius" : ""}
          src={imgUrl}
          alt={`${title}${sideLabel ? ` - ${sideLabel}` : ""}`}
          loading="lazy"
          draggable={false}
        />
        {sideLabel && <div className="is-side">{sideLabel}</div>}
      </div>
    );
  };

  return (
    <div className="is-grid_item">
      <div className="is-main-img-wrapper">
        <div className="is-grid_item_img">
          <div
            className="is-drag-area"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
          >
            <button
              type="button"
              className="img-nav img-nav-left"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={handlePrev}
              aria-label="Previous image"
              disabled={animating || !isReady}
            >
              <FaChevronLeft />
            </button>

            <div
              className="is-slider-viewport-wrapper"
              style={{ position: "relative" }}
            >
              <button
                type="button"
                className="is-img-enlarge"
                aria-label="Enlarge image"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => setModalOpen(true)}
              >
                <FaSearchPlus size={18} />
              </button>

              <div
                className="is-slider-viewport"
                ref={viewportRef}
                style={{ visibility: isReady ? "visible" : "hidden" }}
              >
                <div
                  className="is-slide-track"
                  onTransitionEnd={handleTrackTransitionEnd}
                  style={{
                    transform: getTrackTransform(),
                    transition:
                      !isReady || isDragging || !enableTransition
                        ? "none"
                        : `transform ${SLIDE_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                  }}
                >
                  {renderSlide(prevImg)}
                  {renderSlide(currentImg)}
                  {renderSlide(nextImg)}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="img-nav img-nav-right"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={handleNext}
              aria-label="Next image"
              disabled={animating || !isReady}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>

      <div className="is-img-slider">
        {imgArray.map((img, i) => {
          const thumbSide = getImageSide(img);

          return (
            <button
              key={i}
              type="button"
              className={`is-img-thumb${imgIdx === i ? " active" : ""}`}
              onClick={() => {
                if (animating || i === imgIdx) return;

                setEnableTransition(false);
                setSlideDirection(null);
                setDragX(0);
                setImgIdx(i);

                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    setEnableTransition(true);
                  });
                });
              }}
              aria-label={`View image ${i + 1}${
                thumbSide ? ` - ${thumbSide}` : ""
              }`}
            >
              <img
                src={img}
                alt={`${title} thumbnail ${i + 1}${
                  thumbSide ? ` - ${thumbSide}` : ""
                }`}
                loading="lazy"
                draggable={false}
              />
            </button>
          );
        })}
      </div>

      <ImageModal
        open={modalOpen}
        src={currentImg}
        alt={title}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

export default ImageSlider;
